import {
  CheckCircleOutlined,
  FileDoneOutlined,
  PrinterOutlined,
  StopOutlined,
} from "@ant-design/icons";

/**
 * Лентата с действия под „История на операциите".
 *
 * Рендерира се САМО когато прозорецът е отворен от Справка сметки
 * (`showCancelButton`) — Сторно, Картови плащания, Отстъпки и Промени на цени
 * показват само хронологията. И четирите бутона са ghost с различен тон: двойка
 * от рода „сторнирай всичко / възстанови" няма „правилно" действие, а плътен
 * primary би подсказал точно това.
 */
export function BillOperationsFooter({
  isMobile,
  isNarrow,
  canCancel,
  canRestore,
  canInvoice,
  isInvoiced,
  canPrintReceiptCopy,
  receiptCopyHint,
  cancelLoading,
  restoreLoading,
  receiptCopyLoading,
  onCancelAll,
  onRestoreAll,
  onInvoice,
  onPrintReceiptCopy,
}: {
  isMobile: boolean;
  isNarrow: boolean;
  canCancel: boolean;
  canRestore: boolean;
  canInvoice: boolean;
  isInvoiced: boolean;
  canPrintReceiptCopy: boolean;
  receiptCopyHint: string;
  cancelLoading: boolean;
  restoreLoading: boolean;
  receiptCopyLoading: boolean;
  onCancelAll: () => void;
  onRestoreAll: () => void;
  onInvoice: () => void;
  onPrintReceiptCopy: () => void;
}) {
  return (
    <div
      className={`grid shrink-0 gap-2 border-t border-shade-1 ${
        isNarrow ? "px-4 py-3" : "px-6 py-3.5"
      } ${
        // Четвъртият бутон не се събира до другите три на 600px — там
        // лентата минава на 2×2, вместо да свие етикетите до нечетимост.
        isMobile ? "grid-cols-1" : isNarrow ? "grid-cols-2" : "grid-cols-4"
      }`}
    >
      <FooterButton
        variant="danger"
        icon={<StopOutlined />}
        disabled={!canCancel}
        loading={cancelLoading}
        onClick={onCancelAll}
        label="Сторнирай всичко"
      />
      <FooterButton
        variant="primary"
        icon={<CheckCircleOutlined />}
        disabled={!canRestore}
        loading={restoreLoading}
        onClick={onRestoreAll}
        label="Възстанови"
      />
      <FooterButton
        variant="primary"
        icon={<FileDoneOutlined />}
        disabled={!canInvoice || isInvoiced}
        onClick={onInvoice}
        label="Фактуриране"
      />
      {/* Копие на фискалния бон от КЛЕН на апарата, който го е издал. */}
      <FooterButton
        variant="primary"
        icon={<PrinterOutlined />}
        disabled={!canPrintReceiptCopy}
        loading={receiptCopyLoading}
        onClick={onPrintReceiptCopy}
        label="Дубликат на бон"
        title={receiptCopyHint}
      />
    </div>
  );
}

function FooterButton({
  variant,
  icon,
  disabled,
  loading,
  onClick,
  label,
  title,
}: {
  variant: "primary" | "danger";
  icon: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
  label: string;
  title?: string;
}) {
  const tone =
    variant === "danger"
      ? "border-danger-tint2 text-danger-ink hover:bg-danger-tint"
      : "border-brand-tint2 text-brand-deep-ink hover:bg-brand-tint";

  // `title` стои на ОБВИВКАТА, не на бутона: браузърът не изпраща mouse
  // събития към disabled контрола, тоест native tooltip върху него просто
  // не се показва — а обяснението трябва точно когато бутонът е сив.
  // `grid` прави span-а решетъчния елемент, а бутонът се разтяга в него,
  // така че подредбата на лентата не се променя.
  return (
    <span title={title} className="grid">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || loading}
        className={`inline-flex h-10 items-center justify-center gap-2 rounded-[10px] border bg-transparent px-3.5 text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent ${tone}`}
      >
        {icon}
        <span>{label}</span>
        {loading ? (
          <span className="size-3 animate-spin rounded-full border-2 border-current border-r-transparent" />
        ) : null}
      </button>
    </span>
  );
}
